import { computed, reactive, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

import type { WabunkaAPIError } from '@/models/error';
import { useGlobalWabunkaReservationForm } from '@/wabunka-src/common/features/Reservation/composables/forms/useGlobalWabunkaReservationForm';
import { checkBookerEmail } from '@/wabunka-src/common/features/Reservation/core/requiredFields/fieldChecks';
import { useCheckCodeUsability } from '@/wabunka-src/features/Experience/subFeatures/Reservation/composable/query/useCheckCodeUsability';
import { buildWabunkaCode } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaCode';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';

const CODE_CHECK_FAILED_MESSAGE = "We couldn't check this code. Please try again.";

// 無効なコードはサーバの文言（This code is invalid. 等）をそのまま出す。5xx の文言は開発者向けなので出さない
const buildFailureMessage = (error: WabunkaAPIError | null): string => {
  if (!error || error.status >= 500) return CODE_CHECK_FAILED_MESSAGE;
  return error.getMessage() || CODE_CHECK_FAILED_MESSAGE;
};

/** 新動線の特典コード。Payment と内訳の欄は適用済みのコードを共有し、エラーは欄ごとに持つ。サーバの検証を通ったコードだけをフォームの state に入れる（D-031） */
export const useReservationCodeRedemption = (preReservationPrice: MaybeRefOrGetter<ExperiencePreReservationPriceWithGuestTypeViewModel | undefined>) => {
  const { form } = useGlobalWabunkaReservationForm();

  const isRedeeming = ref(false);
  // コードを変えてから新しい見積もりが届くまで。古い見積もりのまま送ると、決済の要否を古い総額で決めてしまう
  const isAwaitingPrice = ref(false);
  watch(
    () => toValue(preReservationPrice),
    () => {
      isAwaitingPrice.value = false;
    },
  );

  const appliedCodeName = computed(() => form.value.step2.code.codeName);
  // メール限定のコードは空のメールだと検証を通り、送信時の再検証で初めて弾かれる
  const isEmailReady = computed(() => checkBookerEmail(form.value.step2).length === 0);

  const redeem = async (input: string): Promise<string> => {
    const codeName = input.trim();
    if (codeName === '' || isRedeeming.value || !isEmailReady.value) return '';

    isRedeeming.value = true;
    try {
      const { data, error } = await useCheckCodeUsability(codeName, form.value.step2.email);
      if (!data.value || error.value) return buildFailureMessage(error.value);
      form.value.step2.code = { ...buildWabunkaCode(data.value), codeName };
      isAwaitingPrice.value = true;
      return '';
    } finally {
      isRedeeming.value = false;
    }
  };

  const remove = () => {
    form.value.step2.code = { codeName: '', benefitType: undefined, benefitValue: undefined };
    isAwaitingPrice.value = true;
  };

  // 子の template から .value なしで読めるよう、ref を展開して渡す
  return reactive({ appliedCodeName, isEmailReady, isRedeeming, isAwaitingPrice, redeem, remove });
};

export type ReservationCodeRedemption = ReturnType<typeof useReservationCodeRedemption>;
